// Library imports
import React, { useEffect, useRef, useState } from "react";

// User Component imports
import OpsVideoTile from "./OpsVideoTile";
import { VIDEO_SLOTS } from "./appearanceDefs";
import { formatDateTimeSlash } from "../utils/promptVersions";

const VideoCell = ({ slot, asset, appearanceName }) => (
    <div className="ops-video-cell">
        <div className="ops-video-cell-head">
            <span className={"ops-video-cell-label" + (asset ? "" : " is-empty")}>{slot.short}</span>
            {slot.jp && <span className="ops-video-cell-jp" title={slot.jp}>{slot.jp}</span>}
        </div>
        {asset ?
            <OpsVideoTile url={asset.url} title={`${appearanceName} / ${slot.short}`} />
            :
            <div className="ops-video-empty"><span>未登録</span></div>
        }
        <div className="ops-video-cell-path" title={asset ? asset.file_name : ""}>{asset ? asset.file_name : "\u00a0"}</div>
    </div>
);

/**
 * 閉じたカードのサムネイル（idle 動画をループ再生して見た目を伝える）
 * 画面内に入ってから読み込み、画面外では止める
 */
const ThumbVideo = ({ url }) => {
    const videoRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [src, setSrc] = useState(null);

    useEffect(() => {
        const el = videoRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return undefined;
        }
        const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const el = videoRef.current;
        if (!el || !el.src) return;
        if (visible) el.play().catch(() => {});
        else el.pause();
    }, [visible]);

    // 一度画面内に入ったら読み込みを始める（以降は画面の出入りで再生・停止だけ切り替える）
    useEffect(() => { if (visible && !src) setSrc(url); }, [visible, src, url]);

    return <video ref={videoRef} src={src || undefined} muted loop playsInline autoPlay preload="metadata" />;
};

/**
 * 外見動画の1件。閉じている時は idle 動画のサムネイル、開いた時は全種別の動画を表示する（最初に開いているのは適用中のみ）
 */
const AppearanceCard = ({ appearance, isOpen, onToggle, onEdit, onDownload, isDownloading }) => {
    const idle = appearance.assets.idle;
    const hasVideo = VIDEO_SLOTS.some((slot) => appearance.assets[slot.key]);

    return (
        <div className={"ops-appearance-card" + (appearance.is_use ? " is-use" : "")}>
            <div className="ops-appearance-head">
                <button type="button" className="ops-appearance-toggle" aria-expanded={isOpen} onClick={onToggle}
                    title={isOpen ? "動画一覧を閉じます" : "動画一覧を開きます（動画を読み込みます）"}>
                    <span className="ops-appearance-chevron"><i className={"bi bi-chevron-right" + (isOpen ? " is-open" : "")} /></span>
                    <span className={"ops-appearance-thumb" + (isOpen ? " is-hidden" : "")}>
                        {!isOpen && idle && <ThumbVideo url={idle.url} />}
                    </span>
                    <span className="ops-appearance-info">
                        <span className="ops-appearance-name-row">
                            <span className="ops-appearance-name">{appearance.name}</span>
                            {appearance.is_use && <span className="ops-badge is-inuse">適用中</span>}
                        </span>
                        <span className="ops-appearance-meta">
                            <span>登録: {formatDateTimeSlash(appearance.regdate)}</span>
                            <span className="ops-appearance-meta-sep">／</span>
                            <span>外見ID: {appearance.id}</span>
                            <span className="ops-appearance-meta-sep">／</span>
                            <span>ボイスID: {appearance.t_voice_type_id} ({appearance.voice_name || "-"})</span>
                        </span>
                    </span>
                </button>
                <button type="button" className="ops-pill ops-appearance-download" onClick={onDownload} disabled={!hasVideo || isDownloading}
                    title="この外見に登録されている動画をZIPでまとめてダウンロードします">
                    <i className={"bi " + (isDownloading ? "bi-arrow-repeat ops-spin" : "bi-download")} />{isDownloading ? "ダウンロード中…" : "一括ダウンロード"}
                </button>
                <button type="button" className="ops-pill ops-appearance-edit" onClick={onEdit} title="名前の変更・動画の差し替え／追加を行います">編集</button>
            </div>

            {isOpen &&
                <div className="ops-appearance-videos">
                    <div className="ops-appearance-video-group is-main">
                        <div className="ops-appearance-video-title">キャラクター動画</div>
                        <div className="ops-appearance-video-grid">
                            {VIDEO_SLOTS.filter((s) => s.group === 'motion' && (!s.hideWhenEmpty || appearance.assets[s.key])).map((slot) => (
                                <VideoCell key={slot.key} slot={slot} asset={appearance.assets[slot.key]} appearanceName={appearance.name} />
                            ))}
                        </div>
                    </div>
                    <div className="ops-appearance-video-divider" />
                    <div className="ops-appearance-video-group is-widget">
                        <div className="ops-appearance-video-title">ウィジェット表示</div>
                        <div className="ops-appearance-video-grid is-single">
                            {VIDEO_SLOTS.filter((s) => s.group === 'widget').map((slot) => (
                                <VideoCell key={slot.key} slot={slot} asset={appearance.assets[slot.key]} appearanceName={appearance.name} />
                            ))}
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

export default AppearanceCard;
